/**
 * audit.js — the checklist Summary: one row per item over a date window ("how often do
 * we actually order this?"), its learned reorder rhythm, and what looks due now.
 */

import { normalizeName } from './names';
import { localDateISO, daysBetween, inDateRange } from './dates';

/** Log rows inside a window, optionally narrowed to one checklist ('all' = any). */
export function filterAuditLog(log, { from = '', to = '', list = 'all', q = '' } = {}) {
  const needle = normalizeName(q);
  return (log || []).filter(l => {
    if (!l || !inDateRange(l.date, from, to)) return false;
    if (list !== 'all' && l.list !== list) return false;
    if (needle && !normalizeName(l.name).includes(needle)) return false;
    return true;
  });
}

/** Distinct values with counts, most-used first — powers the store columns/breakdown. */
function tally(values) {
  const counts = new Map();
  for (const v of values) {
    const clean = String(v == null ? '' : v).trim();
    if (clean) counts.set(clean, (counts.get(clean) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

/**
 * Roll a checklist completion log up to one row per item — "how many times did we
 * order this, when, from where, how much?".
 *
 * `orders` counts only COMPLETED cycles, so a still-pending add never inflates the
 * count (it lands in `pending` instead). `avgGapDays` is the mean spacing between
 * distinct order dates — a learned reorder interval — and is null below two orders.
 * `qtys` collects quantities where they were recorded (older rows have none).
 *
 * @returns [{ key, name, lists, orders, pending, dates, firstDate, lastDate, daysSince,
 *             avgGapDays, stores, storeLabel, qtys, lastQty, buyers, entries }]
 */
export function auditRollup(log, todayISO) {
  const today = todayISO || localDateISO();
  const byKey = new Map();

  for (const l of log || []) {
    if (!l) continue;
    const key = normalizeName(l.name);
    if (!key) continue;
    let row = byKey.get(key);
    if (!row) {
      row = { key, name: l.name, lists: new Set(), dates: [], storeVals: [], buyerVals: [], qtys: [], orders: 0, pending: 0, entries: [] };
      byKey.set(key, row);
    }
    const pending = (l.status || 'completed') === 'pending';
    if (l.list) row.lists.add(l.list);
    row.entries.push(l);
    if (pending) { row.pending += 1; continue; }

    row.orders += 1;
    if (l.date) row.dates.push(l.date);
    row.storeVals.push(l.store);
    row.buyerVals.push(l.by);
    const qty = String(l.qty == null ? '' : l.qty).trim();
    if (qty) row.qtys.push({ qty, date: l.date || '' });
    // Freshest spelling wins, matching the Today board's catalog.
    if (!row.lastDate || (l.date || '') >= row.lastDate) { row.lastDate = l.date || ''; row.name = l.name; }
  }

  return [...byKey.values()].map(row => {
    const dates = [...new Set(row.dates)].sort();
    const firstDate = dates[0] || '';
    const lastDate = dates[dates.length - 1] || '';
    const stores = tally(row.storeVals);
    const qtys = [...row.qtys].sort((a, b) => b.date.localeCompare(a.date));
    return {
      key: row.key,
      name: row.name,
      lists: [...row.lists],
      orders: row.orders,
      pending: row.pending,
      dates,          // distinct order dates, ascending — plots the rhythm strip
      firstDate,
      lastDate,
      daysSince: lastDate ? daysBetween(lastDate, today) : null,
      // Mean spacing across the window's orders; undefined until there are two to span.
      avgGapDays: dates.length > 1 ? Math.round(daysBetween(firstDate, lastDate) / (dates.length - 1)) : null,
      stores,
      storeLabel: stores.map(s => s.value).join(', '),
      qtys,
      lastQty: qtys.length ? qtys[0].qty : '',
      buyers: tally(row.buyerVals),
      entries: row.entries.sort((a, b) => (b.date || '').localeCompare(a.date || '')),
    };
  }).sort((a, b) => b.orders - a.orders || (b.lastDate || '').localeCompare(a.lastDate || '') || a.name.localeCompare(b.name));
}

/** Headline numbers + store split for a filtered audit window. */
export function auditTotals(log, rows) {
  const list = log || [];
  const completed = list.filter(l => (l.status || 'completed') !== 'pending');
  return {
    orders: completed.length,
    items: (rows || []).length,
    days: new Set(completed.map(l => l.date).filter(Boolean)).size,
    pending: list.length - completed.length,
    stores: tally(completed.map(l => l.store)),
    withQty: completed.filter(l => String(l.qty == null ? '' : l.qty).trim()).length,
  };
}

/** Audit rollup rows → CSV cells parallel to AUDIT_CSV_HEADERS. */
export function auditToCsvRows(rows) {
  return (rows || []).map(r => [
    r.name, r.lists.join(' / '), r.orders, r.pending, r.firstDate, r.lastDate,
    r.daysSince == null ? '' : r.daysSince, r.avgGapDays == null ? '' : r.avgGapDays,
    r.storeLabel, r.lastQty, r.qtys.map(q => q.qty).join(' | '),
  ]);
}

/**
 * Turn the rollup into a decision: which items look due to reorder?
 *
 * An item earns a rhythm once it's been ordered on two separate days — then
 * `avgGapDays` is how often you *actually* buy it, learned rather than configured.
 * Once it's been at least that long since the last order, it's due. `ratio` is how
 * overdue: 1.0 = due today, 2.0 = twice the usual gap (probably already run out).
 *
 * Items back on the active list are skipped — they're already handled.
 */
export function dueForReorder(rows, { onList = new Set(), minOrders = 2 } = {}) {
  return (rows || [])
    .filter(r => r.orders >= minOrders && r.avgGapDays && r.daysSince != null && !onList.has(r.key))
    .map(r => ({ ...r, overdueBy: r.daysSince - r.avgGapDays, ratio: r.daysSince / r.avgGapDays }))
    .filter(r => r.ratio >= 1)
    .sort((a, b) => b.ratio - a.ratio || b.orders - a.orders || a.name.localeCompare(b.name));
}

/**
 * The real span the rollup covers, for plotting the order-rhythm strip. An open
 * `from` (all-time) falls back to the earliest order actually seen, so the ticks
 * spread across the data instead of bunching against an arbitrary epoch.
 */
export function auditWindow(rows, { from = '', to = '' } = {}, todayISO) {
  const end = to || todayISO || localDateISO();
  let start = from;
  if (!start) {
    for (const r of rows || []) {
      if (r.firstDate && (!start || r.firstDate < start)) start = r.firstDate;
    }
  }
  return { start: start || end, end };
}
